/**
 * lib/format.ts — how values are turned into words, and how they are ordered.
 *
 * Two jobs that look unrelated and are not. Formatting decides what a cell SAYS; the comparator decides
 * where that cell SITS. Left to each table, the two disagree — a column formatted "2 days ago" sorted as
 * text puts "3 hours" before "2 days", which is wrong and wrong invisibly. So a column declares its type
 * once, this module formats and orders it accordingly, and 04-data.md §1.2.1's requirements — type-aware
 * ordering, stability, empties grouped at one end regardless of direction — hold everywhere by
 * construction rather than by each table remembering.
 *
 * Timestamps from this API are ISO 8601 in UTC. They are rendered in the viewer's own zone, because the
 * question an operator asks of a run history is "was that during my afternoon", and never sorted on their
 * rendered form.
 */

/** What a value IS, which is what decides how it orders. */
export type ValueType = "text" | "number" | "date" | "boolean";

/** A value a column can yield. `null`/`undefined` mean "no value" and always group at the end. */
export type SortableValue = string | number | boolean | null | undefined;

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const TIME_ONLY = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

/**
 * Locale-aware text ordering. `Intl.Collator` rather than `<`: comparing strings by code point sorts
 * "Zebra" before "apple" and puts every accented name in its own section at the end.
 */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** An absolute timestamp in the viewer's zone. The em dash is the one honest rendering of "never". */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "—";
  return DATE_TIME.format(at);
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return "—";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "—";
  return TIME_ONLY.format(at);
}

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const RELATIVE_STEPS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ["second", 1000],
  ["minute", 60 * 1000],
  ["hour", 60 * 60 * 1000],
  ["day", 24 * 60 * 60 * 1000],
  ["week", 7 * 24 * 60 * 60 * 1000],
  ["month", 30 * 24 * 60 * 60 * 1000],
  ["year", 365 * 24 * 60 * 60 * 1000],
];

/**
 * "3 hours ago", "in 2 days". Always paired with the absolute value in a `title`, never on its own — a
 * relative time is quick to read and impossible to correlate with a log line.
 */
export function formatRelative(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "—";
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return "—";

  const deltaMs = at.getTime() - now.getTime();
  const magnitude = Math.abs(deltaMs);
  if (magnitude < 45_000) return "just now";

  // Walk up the units and use the largest one the delta clears; below a minute the answer is above.
  let chosen: Intl.RelativeTimeFormatUnit = "second";
  let divisor = 1000;
  for (const step of RELATIVE_STEPS) {
    const [unit, ms] = step;
    if (magnitude >= ms) {
      chosen = unit;
      divisor = ms;
    }
  }
  return RELATIVE.format(Math.round(deltaMs / divisor), chosen);
}

/** A duration between two timestamps, as an operator reads it: "4m 12s", "820ms", "—" while running. */
export function formatDuration(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return "—";
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return "—";

  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / 60_000) % 60;
  const hours = Math.floor(ms / 3_600_000);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

const NUMBER = new Intl.NumberFormat();

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return NUMBER.format(value);
}

/** Minutes past local midnight, as the server stores a send time. 540 → "09:00". */
export function formatMinuteOfDay(minute: number | null | undefined): string {
  if (minute === null || minute === undefined) return "—";
  const clamped = Math.max(0, Math.min(1439, Math.trunc(minute)));
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

/**
 * The scheduling fields a schedule can be described from — a `Job`, without importing one.
 *
 * `format.ts` is the leaf that everything else imports, so it takes the shape rather than the type.
 */
export interface SchedulableJob {
  readonly cadence: string;
  readonly timezone: string;
  readonly sendAtMinute: number;
  readonly intervalHours?: number;
}

/**
 * "Every 6 hours, at :30 past" / "Daily at 09:00 Europe/London".
 *
 * The two readings differ because the two schedules do. An interval fires on a MINUTE past the hour —
 * the server takes `send_at_minute % 60` for it — so printing a time of day beside it says something
 * untrue: an hourly job set to 09:00 fires at the top of every hour, not at nine. And an interval has no
 * time zone to name, because a span of hours is the same span everywhere.
 */
export function formatCadence(job: SchedulableJob): string {
  const minutePastHour = Math.abs(Math.trunc(job.sendAtMinute)) % 60;
  const past = `:${String(minutePastHour).padStart(2, "0")} past`;

  if (job.cadence !== "hourly") {
    return `${humanise(job.cadence)} at ${formatMinuteOfDay(job.sendAtMinute)} ${job.timezone}`;
  }

  const hours = Math.trunc(job.intervalHours ?? 1);
  if (hours <= 1) return `Hourly, at ${past}`;
  return `Every ${formatCount(hours)} hours, at ${past}`;
}

/**
 * Roughly how many hours a schedule leaves between updates — the sort key for a frequency column.
 *
 * Approximate on purpose: a month is not a fixed number of hours and does not need to be, because this
 * value is only ever compared with another one. Ordering by the raw cadence word instead would file
 * "every 6 hours" and "hourly" together under `h` and put "monthly" before "weekly".
 */
export function cadenceHours(job: SchedulableJob): number {
  switch (job.cadence) {
    case "hourly":
      return Math.max(1, Math.trunc(job.intervalHours ?? 1));
    case "daily":
      return 24;
    case "weekly":
      return 24 * 7;
    case "fortnightly":
      return 24 * 14;
    case "monthly":
      return 24 * 30;
    default:
      return Number.MAX_SAFE_INTEGER;
  }
}

const WEEKDAYS: readonly string[] = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

/** ISO weekday, 1 = Monday. Indexing yields `string | undefined` under noUncheckedIndexedAccess. */
export function formatWeekday(day: number | null | undefined): string {
  if (day === null || day === undefined) return "—";
  return WEEKDAYS[day - 1] ?? "—";
}

/** An enum from the wire, as a sentence: `business_units` → "Business units". */
export function humanise(value: string): string {
  const spaced = value.replace(/[_-]+/g, " ").trim();
  if (spaced === "") return "";
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * A scope role, as the interface names it — not as `humanise` guesses it.
 *
 * `humanise` is a generic prettifier over a wire enum, and it gets `job_author` right by luck and
 * `bu_admin` wrong: it renders **"Bu admin"**, on the very screens whose own role picker offers
 * "Business-unit admin". One name for one thing, in one place, so the picker and the badge cannot say
 * different words about the same grant again. Unknown values still fall through to `humanise`, because a
 * role this table has not heard of should look untidy rather than disappear.
 */
const SCOPE_ROLE_LABELS: Readonly<Record<string, string>> = {
  job_author: "Job author",
  goal_author: "Goal author",
  bu_admin: "Business-unit admin",
};

export function roleLabel(role: string): string {
  return SCOPE_ROLE_LABELS[role] ?? humanise(role);
}

/**
 * A §3.4 resource axis, as the interface names it — for the same reason `roleLabel` exists, and with a
 * sharper edge.
 *
 * `humanise` renders `confluence_space` as "Confluence space" by luck and `repo` as "Repo", which is
 * jargon on a screen an administrator reads to understand what a group confers. One name for one thing,
 * in one place, so the rule list and the picker cannot say different words about the same grant.
 */
const RESOURCE_KIND_LABELS: Readonly<Record<string, string>> = {
  repo: "Repository",
  path: "Path",
  confluence_space: "Confluence space",
  slack_channel: "Slack channel",
};

export function resourceKindLabel(kind: string): string {
  return RESOURCE_KIND_LABELS[kind] ?? humanise(kind);
}

/**
 * What an update SAYS — never what it is called, and never where it goes.
 *
 * A closed map rather than `humanise`, which would render `executive_summary` as "Executive summary"
 * correctly and `security_overview` as "Security overview" correctly and then, the day a fourth kind
 * arrives with a name that does not survive underscore-splitting, quietly get it wrong on every screen
 * at once. One name for one thing, in one place.
 */
const UPDATE_KIND_LABELS: Readonly<Record<string, string>> = {
  changelog: "Changelog",
  executive_summary: "Executive summary",
  security_overview: "Security overview",
};

export function updateKindLabel(kind: string): string {
  return UPDATE_KIND_LABELS[kind] ?? humanise(kind);
}

/**
 * The one-line gloss under a kind, where a screen has room for it. Deliberately about the SOURCE, since
 * that is the whole of the difference: a changelog reads commits, the other two read a document.
 */
const UPDATE_KIND_HINTS: Readonly<Record<string, string>> = {
  changelog: "What changed, from the commits themselves",
  executive_summary: "What the product is worth, from each repository's EXECUTIVE.md",
  security_overview: "How it is kept safe, from each repository's SECURITY.md",
};

export function updateKindHint(kind: string): string {
  return UPDATE_KIND_HINTS[kind] ?? "";
}

/**
 * One or two letters for the account avatar. Never empty, never longer than two.
 *
 * There is no image to fall back FROM: `Me.user` carries an id, an address, a display name, a status
 * and the superuser flag, and nothing else — no avatar url exists anywhere on the wire, and neither
 * sibling application has one either. So initials are not a placeholder, they are the rendering.
 *
 * The address's local part is split on the separators a directory actually produces
 * (`operations.lead@…` → "OL"), because an address is the one identifier every account has: a person
 * who has never signed in has no display name at all.
 */
export function initials(displayName: string | null | undefined, email: string): string {
  const named = (displayName ?? "").trim().split(/\s+/).filter((part) => part !== "");
  if (named.length > 0) return pairOf(named);

  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter((part) => part !== "");
  if (parts.length > 0) return pairOf(parts);

  // An address with no local part at all — "@example.com", or the empty string. Untidy input deserves
  // an untidy answer rather than an empty circle that reads as a rendering fault.
  return "?";
}

/** First letter of the first token, plus the first letter of the last — one letter when there is one token. */
function pairOf(parts: readonly string[]): string {
  const first = parts[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? "") : "";
  const letters = `${first.charAt(0)}${last.charAt(0)}`;
  return letters === "" ? "?" : letters.toUpperCase();
}

/** Truncate for a cell, at a word boundary where one is near enough to be worth using. */
export function truncate(value: string, max = 80): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${space > max * 0.6 ? cut.slice(0, space) : cut}…`;
}

/**
 * Order two values of a declared type.
 *
 * Two rules are not negotiable and are why this is one function rather than a `<` at each call site:
 *
 *   - **Empty groups at the end in BOTH directions.** A missing value is not "smallest"; interleaving
 *     blanks with real data on a descending sort is how a user concludes the sort is broken.
 *   - **The type decides the comparison.** Numbers numerically (`9` before `10`), dates chronologically,
 *     text by collation. Sorting a numeric column as text is a defect, not a nuance.
 *
 * The caller adds stability by tie-breaking on the row's original index; doing it here would need the
 * index, and a comparator that silently reorders ties is the reason a refetched table reshuffles.
 */
export function compareValues(a: SortableValue, b: SortableValue, type: ValueType): number {
  const aEmpty = a === null || a === undefined || a === "";
  const bEmpty = b === null || b === undefined || b === "";
  // Returned unsigned by direction: the caller flips the comparison, not this, so empties stay put.
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  switch (type) {
    case "number": {
      const left = typeof a === "number" ? a : Number(a);
      const right = typeof b === "number" ? b : Number(b);
      if (Number.isNaN(left) && Number.isNaN(right)) return 0;
      if (Number.isNaN(left)) return 1;
      if (Number.isNaN(right)) return -1;
      return left < right ? -1 : left > right ? 1 : 0;
    }
    case "date": {
      const left = new Date(String(a)).getTime();
      const right = new Date(String(b)).getTime();
      if (Number.isNaN(left) && Number.isNaN(right)) return 0;
      if (Number.isNaN(left)) return 1;
      if (Number.isNaN(right)) return -1;
      return left - right;
    }
    case "boolean": {
      const left = a === true ? 1 : 0;
      const right = b === true ? 1 : 0;
      return left - right;
    }
    case "text":
    default:
      return COLLATOR.compare(String(a), String(b));
  }
}
